import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { Expense, DriverSettings, DEFAULT_SETTINGS } from '../types';

interface TaxTrackerDB extends DBSchema {
  expenses: {
    key: string;
    value: Expense;
    indexes: {
      'by-category': string;
      'by-date': string;
      'by-month-year': string;
    };
  };
  settings: {
    key: string;
    value: DriverSettings;
  };
}

const DB_NAME = 'tax-expense-tracker';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<TaxTrackerDB> | null = null;

export async function getDB(): Promise<IDBPDatabase<TaxTrackerDB>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<TaxTrackerDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Expenses store
      if (!db.objectStoreNames.contains('expenses')) {
        const expenseStore = db.createObjectStore('expenses', { keyPath: 'id' });
        expenseStore.createIndex('by-category', 'category');
        expenseStore.createIndex('by-date', 'date');
        expenseStore.createIndex('by-month-year', ['year', 'month']);
      }

      // Settings store
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'id' });
      }
    },
  });

  return dbInstance;
}

// Expense operations
export async function addExpense(expense: Expense): Promise<void> {
  const db = await getDB();
  await db.put('expenses', expense);
}

export async function getExpense(id: string): Promise<Expense | undefined> {
  const db = await getDB();
  return db.get('expenses', id);
}

export async function getAllExpenses(): Promise<Expense[]> {
  const db = await getDB();
  return db.getAll('expenses');
}

export async function getExpensesByCategory(category: string): Promise<Expense[]> {
  const db = await getDB();
  return db.getAllFromIndex('expenses', 'by-category', category);
}

export async function getExpensesByMonthYear(year: number, month: number): Promise<Expense[]> {
  const db = await getDB();
  const all = await db.getAll('expenses');
  return all.filter(e => e.year === year && e.month === month);
}

export async function deleteExpense(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('expenses', id);
}

export async function updateExpense(expense: Expense): Promise<void> {
  const db = await getDB();
  await db.put('expenses', expense);
}

// Settings operations
export async function getSettings(): Promise<DriverSettings> {
  const db = await getDB();
  const settings = await db.get('settings', 'driver-settings');
  return settings || DEFAULT_SETTINGS;
}

export async function saveSettings(settings: DriverSettings): Promise<void> {
  const db = await getDB();
  await db.put('settings', { ...settings, id: 'driver-settings' } as DriverSettings & { id: string });
}

// Calculate storage used
export async function calculateStorageUsed(): Promise<{ bytes: number; formatted: string }> {
  const expenses = await getAllExpenses();
  let totalBytes = 0;

  for (const expense of expenses) {
    totalBytes += expense.fileSize || 0;
  }

  let formatted: string;
  if (totalBytes < 1024) {
    formatted = `${totalBytes} B`;
  } else if (totalBytes < 1024 * 1024) {
    formatted = `${(totalBytes / 1024).toFixed(1)} KB`;
  } else if (totalBytes < 1024 * 1024 * 1024) {
    formatted = `${(totalBytes / (1024 * 1024)).toFixed(1)} MB`;
  } else {
    formatted = `${(totalBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  return { bytes: totalBytes, formatted };
}

// Export data as JSON backup
export async function exportData(): Promise<string> {
  const expenses = await getAllExpenses();
  const settings = await getSettings();
  return JSON.stringify({ expenses, settings, exportDate: new Date().toISOString() }, null, 2);
}

// Import data from JSON backup
export async function importData(jsonData: string): Promise<{ imported: number; errors: number }> {
  const db = await getDB();
  const data = JSON.parse(jsonData);
  let imported = 0;
  let errors = 0;

  if (data.expenses && Array.isArray(data.expenses)) {
    for (const expense of data.expenses) {
      try {
        await db.put('expenses', expense);
        imported++;
      } catch {
        errors++;
      }
    }
  }

  if (data.settings) {
    await saveSettings(data.settings);
  }

  return { imported, errors };
}





